import {
  bnCountdownLine,
  emptyAffiliateCommission,
  fetchReferrals,
  formatCommissionMoney,
  formatCountdown,
  transferCommission,
} from './helpers.js';

export function createAffiliateCommissionPanel({ useState, useEffect, jsx, jsxs }) {
  function AffiliateCommissionPanel({ t, stats, onRefresh }) {
    const affiliate = stats?.customerType === 'affiliate';
    const [amount, setAmount] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [remaining, setRemaining] = useState(Number(stats?.remainingSeconds) || 0);

    useEffect(() => {
      setRemaining(Number(stats?.remainingSeconds) || 0);
    }, [stats?.remainingSeconds, stats?.nextTransferAt]);

    useEffect(() => {
      if (!(remaining > 0)) return undefined;
      const id = window.setInterval(() => {
        setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => window.clearInterval(id);
    }, [remaining > 0]);

    if (!affiliate) return null;

    const balance = Number(stats?.commissionBalance || 0);
    const canTransfer = remaining <= 0 && balance > 0;
    const negative = balance < 0;

    const onTransfer = async () => {
      const n = Number(amount);
      if (!Number.isFinite(n) || !(n > 0)) {
        setNotice(t('affTransferInvalid') || 'Enter an amount greater than 0');
        return;
      }
      setBusy(true);
      setNotice('');
      try {
        const key = `aff-tx-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        await transferCommission(n, key);
        setAmount('');
        setNotice(t('affTransferSuccess') || 'Transferred to Main Wallet');
        if (onRefresh) await onRefresh();
      } catch (err) {
        setNotice(err.message || (t('affTransferFailed') || 'Transfer failed'));
      } finally {
        setBusy(false);
      }
    };

    return jsxs('div', {
      className: 'aff-commission-box',
      children: [
        jsxs('div', {
          className: 'info-row-wrap',
          children: [
            jsxs('div', {
              className: 'info-row',
              children: [
                jsx('span', { className: 'info-label', children: t('affCommissionRate') || 'Commission Rate' }),
                jsx('span', { className: 'info-value', children: `${Number(stats?.commissionRate || 0)}%` }),
              ],
            }),
          ],
        }),
        jsxs('div', {
          className: 'info-row-wrap',
          children: [
            jsxs('div', {
              className: 'info-row',
              children: [
                jsx('span', { className: 'info-label', children: t('affCommissionBalance') || 'Commission Balance' }),
                jsx('span', {
                  className: negative ? 'info-value money-negative aff-commission-negative' : 'info-value',
                  children: formatCommissionMoney(balance),
                }),
              ],
            }),
          ],
        }),
        remaining > 0
          ? jsxs('div', {
            className: 'aff-cooldown',
            children: [
              jsx('p', {
                className: 'aff-cooldown-copy',
                style: { whiteSpace: 'pre-line' },
                children: bnCountdownLine(remaining),
              }),
              jsx('p', { className: 'aff-countdown-live', children: formatCountdown(remaining) }),
            ],
          })
          : null,
        jsxs('div', {
          className: 'info-row-wrap',
          children: [
            jsx('label', {
              className: 'info-label',
              children: t('affTransferAmount') || 'Transfer to Main Wallet',
            }),
            jsx('input', {
              type: 'number',
              min: '0.01',
              step: '0.01',
              className: 'aff-transfer-input',
              value: amount,
              disabled: !canTransfer || busy,
              onChange: (e) => setAmount(e.target.value),
            }),
          ],
        }),
        jsx('button', {
          type: 'button',
          className: 'info-add-btn',
          disabled: !canTransfer || busy,
          onClick: onTransfer,
          children: t('affTransferBtn') || 'Transfer',
        }),
        notice ? jsx('p', { className: 'referral-notice', children: notice }) : null,
      ],
    });
  }

  return AffiliateCommissionPanel;
}

export { emptyAffiliateCommission, fetchReferrals };
